// ============================================================
// ACCOUNT-SHARING GUARD CLIENT (p2026 #616, infra ADR-42)
// Port of ide-service ide/src/plugins/guard.js + components/guard/banner.vue
// (ide.pythaverse.space, live since 2026-10-16) to the ide2 codebase.
//
// The guard lives on WordPress (mu-plugin ptv-account-guard.php):
// - status: is this account flagged for sharing (1 warning, 2 countdown before
//   it is disabled, 3 disabled)?
// - robots: at most N different Leanbots per account per day; the IDE asks
//   before uploading to a robot it has not used today and reports the robot
//   after the upload.
//
// Load on WordPress matters (thousands of students): the endpoint
// (account-guard/api.php) loads WordPress without its plugins, every call is a
// CORS "simple" request (POST, text/plain body carrying the eID access token,
// so no preflight), and the IDE asks rarely:
// - the first status check waits a random 5-120 s after the IDE opens;
// - then every `poll` seconds from the server (30 min clear / 10 min flagged), +-20%;
// - robots already counted today (the server's list, kept here) need no call.
// Every call fails open: if WordPress can't be reached, nothing is blocked.
// ============================================================

export const GUARD_API_PROD = "https://pythaverse.space/wp-content/mu-plugins/account-guard/api.php";
export const GUARD_API_QA   = "https://qa.pythaverse.space/wp-content/mu-plugins/account-guard/api.php";

// The guard only accepts tokens from the eID its WordPress trusts, so the API
// follows the IDE's eID: prod eID -> prod guard, QA eID -> QA guard, any other
// eID (e.g. eid.leanbot.space) -> no guard. IDEConfig.AccountGuard.api wins.
export function resolveGuardApi(guardConfig, keycloakConfig) {
  if (guardConfig && guardConfig.Enabled === false) return "";
  if (guardConfig && typeof guardConfig.api === "string" && guardConfig.api) return guardConfig.api;
  let host = "";
  try { host = new URL(keycloakConfig?.url || "").hostname; } catch (e) { host = ""; }
  if (host === "eid.pythaverse.space") return GUARD_API_PROD;
  if (host === "id.leanbot.space") return GUARD_API_QA;
  return "";
}

// "LB123456", "Leanbot_123456", "lb 123456 BLE" -> "LB123456"; anything else -> ""
export function normaliseRobot(raw) {
  const m = /(?:leanbot|lb)[_ ]?(\d{4,8})/i.exec(raw || "");
  return m ? `LB${m[1]}` : "";
}

// The server counts robots per day in Vietnam time.
export function todayVN(now = Date.now()) {
  return new Date(now + 7 * 3600 * 1000).toISOString().slice(0, 10);
}

const LANG_KEY = "ptutor.lang"; // same language as the PTutor panel
const DISMISS_KEY = "guard.dismissed";

export const LABELS = {
  vi: {
    warnTitle: "Tài khoản này đang được dùng trên nhiều máy cùng lúc.",
    warnText: "Mỗi học sinh cần một tài khoản riêng. Hãy hỏi thầy cô để có tài khoản của em.",
    cdTitle: "Tài khoản này đang được dùng trên nhiều máy cùng lúc.",
    cdText: "Tài khoản sẽ bị khóa sau {left}. Nếu đây là nhầm lẫn, hãy bấm \"Yêu cầu xem xét\".",
    cdTextWarnOnly: "Tài khoản dùng chung có thể bị khóa. Mỗi học sinh cần một tài khoản riêng.",
    pausedText: "Đã gửi yêu cầu xem xét: quản trị viên Pythaverse sẽ kiểm tra. Thời gian khóa tạm dừng.",
    offTitle: "Tài khoản này đã bị khóa vì được dùng chung.",
    offText: "Hãy liên hệ thầy cô hoặc bộ phận hỗ trợ Pythaverse.",
    request: "Yêu cầu xem xét", dismiss: "Ẩn hôm nay",
    requestTitle: "Yêu cầu xem xét tài khoản",
    requestHelp: "Quản trị viên Pythaverse sẽ xem lại. Trong lúc chờ, tài khoản chưa bị khóa (tối đa 3 ngày).",
    noteLabel: "Vì sao tài khoản được dùng trên nhiều máy? (không bắt buộc)",
    cancel: "Hủy", send: "Gửi", sent: "Đã gửi yêu cầu", failed: "Không gửi được, hãy thử lại",
    robotTitle: "Đã đạt giới hạn Leanbot hôm nay",
    robotText: "Hôm nay tài khoản này đã nạp code cho {limit} Leanbot khác nhau, nên không thể nạp cho {robot}. Mỗi tài khoản chỉ dùng tối đa {limit} Leanbot mỗi ngày.",
    robotToday: "Leanbot đã dùng hôm nay:",
    robotNoteLabel: "Cần thêm Leanbot? Ghi lý do (vd. chuẩn bị phòng lab)",
    robotAsk: "Xin thêm Leanbot", robotSent: "Đã gửi yêu cầu. Quản trị viên sẽ xem xét.", close: "Đóng",
    hours: "{h} giờ {m} phút", minutes: "{m} phút",
  },
  en: {
    warnTitle: "This account is being used on many computers at the same time.",
    warnText: "Each student needs their own account. Please ask your teacher for yours.",
    cdTitle: "This account is being used on many computers at the same time.",
    cdText: "It will be disabled in {left}. If this is a mistake, press \"Request review\".",
    cdTextWarnOnly: "Shared accounts can be disabled. Each student needs their own account.",
    pausedText: "Review requested: a Pythaverse admin will check it. The countdown is paused.",
    offTitle: "This account was disabled because it was shared.",
    offText: "Please contact your teacher or Pythaverse support.",
    request: "Request review", dismiss: "Hide today",
    requestTitle: "Request a review",
    requestHelp: "A Pythaverse admin will look at this account. While you wait, it stays enabled (up to 3 days).",
    noteLabel: "Why is this account used on many computers? (optional)",
    cancel: "Cancel", send: "Send", sent: "Request sent", failed: "Could not send. Please try again.",
    robotTitle: "Leanbot limit reached for today",
    robotText: "This account has already programmed {limit} different Leanbots today, so it can't program {robot}. Each account can use up to {limit} Leanbots a day.",
    robotToday: "Leanbots used today:",
    robotNoteLabel: "Need more Leanbots? Tell us why (e.g. setting up a lab)",
    robotAsk: "Ask for more Leanbots", robotSent: "Request sent. An admin will review it.", close: "Close",
    hours: "{h} h {m} min", minutes: "{m} min",
  },
};

export function fill(s, vars) {
  return s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
}

function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* ignore */ } }

function labels() {
  let lang = lsGet(LANG_KEY);
  if (!lang) lang = (navigator.language || "vi").toLowerCase().indexOf("en") === 0 ? "en" : "vi";
  return LABELS[lang] || LABELS.vi;
}

// Pure: which banner (if any) a status shows. Exported for the tests.
export function bannerFor(status, t, { secondsLeft = 0, dismissedToday = false } = {}) {
  const level = status ? status.level : 0;
  if (level === 1) {
    if (dismissedToday) return null;
    return { kind: "warning", title: t.warnTitle, text: t.warnText, canDismiss: true };
  }
  if (level === 2) {
    const h = Math.floor(secondsLeft / 3600);
    const m = Math.floor((secondsLeft % 3600) / 60);
    const left = h > 0 ? fill(t.hours, { h, m }) : fill(t.minutes, { m: Math.max(1, m) });
    let text = status.willDisable ? fill(t.cdText, { left }) : t.cdTextWarnOnly;
    if (status.requested) text = t.pausedText;
    return { kind: "error", title: t.cdTitle, text, canRequest: !status.requested };
  }
  if (level === 3) return { kind: "error", title: t.offTitle, text: t.offText };
  return null;
}

export class AccountGuard {
  #api = "";
  #getToken = () => "";
  #fetch = null;
  #status = null;
  #fetchedAt = 0;
  #known = { day: "", robots: [] };
  #timer = null;
  #tick = null;
  #started = false;
  #el = {};

  constructor({ api, getToken, fetchImpl } = {}) {
    this.#api = api || "";
    this.#getToken = getToken || (() => "");
    this.#fetch = fetchImpl || ((...a) => window.fetch(...a));
  }

  get enabled() { return Boolean(this.#api); }
  get status() { return this.#status; }

  async #call(action, extra = {}) {
    const token = this.#getToken();
    if (!this.#api || !token) return null;
    const res = await this.#fetch(this.#api, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: JSON.stringify({ ...extra, action, token }),
    });
    if (!res.ok) throw new Error(`guard ${action}: HTTP ${res.status}`);
    return res.json();
  }

  #remember(robots) {
    if (robots && robots.day) this.#known = { day: robots.day, robots: robots.today || [] };
  }

  #setStatus(s) {
    if (!s) return;
    this.#status = s;
    this.#fetchedAt = Date.now();
    this.#remember(s.robots);
    this.render();
  }

  #schedule(seconds, jitter = true) {
    clearTimeout(this.#timer);
    const s = jitter ? Math.max(60, seconds || 1800) * (0.8 + Math.random() * 0.4) : seconds;
    this.#timer = setTimeout(() => this.refreshStatus(), s * 1000);
  }

  async refreshStatus() {
    try {
      this.#setStatus(await this.#call("status"));
    } catch (e) {
      console.warn(e);
    }
    this.#schedule(this.#status && this.#status.poll);
  }

  async requestReview(note) {
    this.#setStatus(await this.#call("request", { note }));
  }

  async requestRobots(robot, note) {
    const r = await this.#call("robot-request", { robot, note });
    if (r && this.#status) this.#status = { ...this.#status, robots: r };
    return r;
  }

  // phase 'check' before an upload (the ID of the connected robot), 'done' after it.
  // Returns false only when the guard refuses this robot today (fails open otherwise).
  async robotUse(raw, phase) {
    const robot = normaliseRobot(raw);
    if (!robot || robot === "LB999999") return true;
    if (this.#known.day === todayVN() && this.#known.robots.indexOf(robot) !== -1) return true;
    try {
      const r = await this.#call("robot", { robot, phase });
      if (!r) return true;
      this.#remember(r);
      if (this.#status) this.#status = { ...this.#status, robots: r };
      if (r.allowed === false) this.showRobotRefused(r);
      return r.allowed !== false;
    } catch (e) {
      console.warn(e);
      return true;
    }
  }

  secondsLeft() {
    const s = this.#status;
    if (!s || !s.deadline) return 0;
    return Math.max(0, (s.secondsLeft || 0) - Math.floor((Date.now() - this.#fetchedAt) / 1000));
  }

  /* ================= UI ================= */

  // host: the element between the top bar and the workspace (body is a flex
  // column, so a visible banner pushes the editor down instead of covering it).
  attach(host, { firstCheckSeconds } = {}) {
    if (!this.enabled || this.#started || !host) return;
    this.#started = true;
    this.#el.host = host;
    this.#tick = setInterval(() => this.render(), 30000);
    const first = firstCheckSeconds !== undefined ? firstCheckSeconds : 5 + Math.random() * 115;
    this.#schedule(first, false);
  }

  render() {
    const host = this.#el.host;
    if (!host) return;
    const t = labels();
    const dismissedToday = lsGet(DISMISS_KEY) === new Date().toDateString();
    const b = bannerFor(this.#status, t, { secondsLeft: this.secondsLeft(), dismissedToday });
    host.replaceChildren();
    host.hidden = !b;
    if (!b) return;
    const bar = document.createElement("div");
    bar.className = `guard-banner guard-${b.kind}`;
    bar.setAttribute("role", b.kind === "error" ? "alert" : "status");
    const text = document.createElement("div");
    text.className = "guard-text";
    const strong = document.createElement("strong");
    strong.textContent = b.title;
    const line = document.createElement("div");
    line.textContent = b.text;
    text.append(strong, line);
    const actions = document.createElement("div");
    actions.className = "guard-actions";
    if (b.canRequest) actions.append(this.#button(t.request, "guard-btn-primary", () => this.#openReview()));
    if (b.canDismiss) {
      actions.append(this.#button(t.dismiss, "guard-btn-text", () => {
        lsSet(DISMISS_KEY, new Date().toDateString());
        this.render();
      }));
    }
    bar.append(text, actions);
    host.append(bar);
  }

  #button(label, cls, onClick) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = cls;
    b.textContent = label;
    b.addEventListener("click", onClick);
    return b;
  }

  #toast(text) {
    const el = document.createElement("div");
    el.className = "guard-toast";
    el.textContent = text;
    document.body.append(el);
    setTimeout(() => el.remove(), 4000);
  }

  // A native <dialog>: PTutorDrawer already leaves Esc to any open dialog.
  #dialog({ title, paragraphs = [], noteLabel, primary, onPrimary }) {
    const t = labels();
    const dlg = document.createElement("dialog");
    dlg.className = "guard-dialog";
    const h = document.createElement("h3");
    h.textContent = title;
    dlg.append(h);
    for (const p of paragraphs) {
      const el = document.createElement("p");
      el.textContent = p.text;
      if (p.small) el.className = "guard-small";
      dlg.append(el);
    }
    let area = null;
    if (noteLabel) {
      const label = document.createElement("label");
      label.textContent = noteLabel;
      area = document.createElement("textarea");
      area.maxLength = 500;
      area.rows = 3;
      label.append(area);
      dlg.append(label);
    }
    const row = document.createElement("div");
    row.className = "guard-actions guard-dialog-actions";
    const close = () => { dlg.close(); dlg.remove(); };
    row.append(this.#button(primary ? t.cancel : t.close, "guard-btn-text", close));
    if (primary) {
      const btn = this.#button(primary, "guard-btn-primary", async () => {
        btn.disabled = true;
        try {
          await onPrimary(area ? area.value : "", { close, dlg, row, btn });
        } finally {
          btn.disabled = false;
        }
      });
      row.append(btn);
    }
    dlg.append(row);
    document.body.append(dlg);
    dlg.addEventListener("close", () => dlg.remove());
    dlg.showModal();
    return dlg;
  }

  #openReview() {
    const t = labels();
    this.#dialog({
      title: t.requestTitle,
      paragraphs: [{ text: t.requestHelp }],
      noteLabel: t.noteLabel,
      primary: t.send,
      onPrimary: async (note, { close }) => {
        try {
          await this.requestReview(note);
          close();
          this.#toast(t.sent);
        } catch (e) {
          this.#toast(t.failed);
        }
      },
    });
  }

  showRobotRefused(r) {
    const t = labels();
    const paragraphs = [{ text: fill(t.robotText, { limit: r.limit || 5, robot: r.robot || "Leanbot" }) }];
    if (r.today && r.today.length) paragraphs.push({ text: `${t.robotToday} ${r.today.join(", ")}`, small: true });
    this.#dialog({
      title: t.robotTitle,
      paragraphs,
      noteLabel: t.robotNoteLabel,
      primary: t.robotAsk,
      onPrimary: async (note, { dlg, row, btn }) => {
        try {
          await this.requestRobots(r.robot, note);
          dlg.querySelector("label")?.remove();
          btn.remove();
          const ok = document.createElement("p");
          ok.className = "guard-ok";
          ok.textContent = t.robotSent;
          dlg.insertBefore(ok, row);
        } catch (e) {
          this.#toast(t.failed);
        }
      },
    });
  }
}
